// In-memory stock for demo mode (no Supabase configured). Mirrors the seed's
// opening balances so the screen looks the same with and without a database.

import type { Page } from "@/components/DataList";
import type { ItemStock, StockBalance, StockListParams, StockMovement, StockRow } from "./api";

const PAGE = 50;
const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));
const opened = new Date(Date.now() - 3 * 86_400_000).toISOString();

interface Seeded {
  item: string;
  description: string;
  type: StockRow["item_type"];
  uom: string;
  decimals: number;
  warehouse: string;
  location: string;
  qty: number;
  lot?: string;
  expiry?: string;
}

const seeded: Seeded[] = [
  { item: "FR-2210", description: "Seat frame, steel", type: "material", uom: "PCS", decimals: 0, warehouse: "MAIN", location: "A-01-01", qty: 120 },
  { item: "FR-2215", description: "Seat frame, aluminium", type: "material", uom: "PCS", decimals: 0, warehouse: "MAIN", location: "A-01-02", qty: 40 },
  { item: "FOAM-45", description: "Seat foam 45 kg/m³", type: "material", uom: "PCS", decimals: 0, warehouse: "MAIN", location: "A-02-01", qty: 80, lot: "F45-2610-01", expiry: "2027-04-30" },
  { item: "FOAM-45", description: "Seat foam 45 kg/m³", type: "material", uom: "PCS", decimals: 0, warehouse: "MAIN", location: "A-02-01", qty: 35, lot: "F45-2609-03", expiry: "2027-02-28" },
  { item: "FOAM-60", description: "Seat foam 60 kg/m³", type: "material", uom: "PCS", decimals: 0, warehouse: "MAIN", location: "A-02-02", qty: 30, lot: "F60-2610-01", expiry: "2027-05-31" },
  { item: "FAB-GR3", description: "Seat fabric, grey", type: "material", uom: "M", decimals: 3, warehouse: "MAIN", location: "B-01-01", qty: 450.5, lot: "GR3-26-118" },
  { item: "FAB-BK1", description: "Seat fabric, black", type: "material", uom: "M", decimals: 3, warehouse: "MAIN", location: "B-01-02", qty: 300, lot: "BK1-26-090" },
  { item: "LTH-BR2", description: "Leather, brown", type: "material", uom: "M2", decimals: 3, warehouse: "MAIN", location: "B-02-01", qty: 85.25, lot: "LTH-7731" },
  { item: "SC-M6", description: "Screw M6×20", type: "material", uom: "PCS", decimals: 0, warehouse: "MAIN", location: "A-03-01", qty: 6000 },
  { item: "SC-M8", description: "Screw M8×25", type: "material", uom: "PCS", decimals: 0, warehouse: "MAIN", location: "A-03-02", qty: 4000 },
  { item: "NUT-M6", description: "Nut M6", type: "material", uom: "PCS", decimals: 0, warehouse: "MAIN", location: "A-03-03", qty: 8000 },
  { item: "GLUE-PU2", description: "PU adhesive", type: "material", uom: "KG", decimals: 3, warehouse: "MAIN", location: "B-03-01", qty: 42.5, lot: "PU2-2610", expiry: "2027-01-31" },
  { item: "CLN-IPA", description: "Isopropanol", type: "material", uom: "L", decimals: 3, warehouse: "MAIN", location: "B-03-02", qty: 20, lot: "IPA-55120", expiry: "2028-10-31" },
  { item: "PRM-07", description: "Primer", type: "material", uom: "L", decimals: 3, warehouse: "MAIN", location: "B-03-03", qty: 15, lot: "PRM-0926", expiry: "2027-09-30" },
  { item: "BOX-600", description: "Carton box 600×400", type: "material", uom: "PCS", decimals: 0, warehouse: "MAIN", location: "A-04-01", qty: 600 },
  { item: "LBL-A6", description: "Shipping label A6", type: "material", uom: "PCS", decimals: 0, warehouse: "MAIN", location: "A-04-02", qty: 5000 },
  { item: "FR-2210", description: "Seat frame, steel", type: "material", uom: "PCS", decimals: 0, warehouse: "PROD", location: "LINE-1", qty: 12 },
  { item: "FOAM-45", description: "Seat foam 45 kg/m³", type: "material", uom: "PCS", decimals: 0, warehouse: "PROD", location: "LINE-1", qty: 10, lot: "F45-2610-01", expiry: "2027-04-30" },
  { item: "CUS-600", description: "Seat cushion assembly", type: "semi_finished", uom: "PCS", decimals: 0, warehouse: "PROD", location: "KIT", qty: 25 },
  { item: "FOAM-60", description: "Seat foam 60 kg/m³", type: "material", uom: "PCS", decimals: 0, warehouse: "QUAR", location: "Q-01", qty: 20, lot: "F60-2610-QC", expiry: "2027-05-31" },
];

const itemId = (code: string) => `item-${code}`;
const warehouseId = (code: string) => `wh-${code}`;

export const demoApi = {
  async listStock(p: StockListParams, cursor: string | null): Promise<Page<StockRow>> {
    await delay(70);
    const q = p.q.trim().toLocaleLowerCase();
    const byItem = new Map<string, StockRow>();
    for (const s of seeded) {
      if (p.warehouseId && warehouseId(s.warehouse) !== p.warehouseId) continue;
      if (q && !`${s.item} ${s.description}`.toLocaleLowerCase().includes(q)) continue;
      const row = byItem.get(s.item) ?? {
        item_id: itemId(s.item),
        code: s.item,
        description: s.description,
        item_type: s.type,
        lot_controlled: !!s.lot,
        base_uom_code: s.uom,
        base_uom_decimals: s.decimals,
        on_hand: 0,
        reserved: 0,
        available: 0,
        locations: 0,
      };
      row.on_hand = Number(row.on_hand) + s.qty;
      row.available = row.on_hand;
      row.locations += 1;
      byItem.set(s.item, row);
    }
    const rows = [...byItem.values()].sort((a, b) => (a.code < b.code ? -1 : 1)).filter((r) => !cursor || r.code > cursor);
    const page = rows.slice(0, PAGE);
    return { rows: page, nextCursor: rows.length > PAGE ? page[page.length - 1]!.code : null };
  },
  async getItemStock(id: string): Promise<ItemStock> {
    await delay(50);
    const rows = seeded.filter((s) => itemId(s.item) === id);
    const balances: StockBalance[] = rows.map((s) => ({
      warehouse_id: warehouseId(s.warehouse),
      warehouse_code: s.warehouse,
      company_code: "PL01",
      location_id: `${s.warehouse}-${s.location}`,
      location_code: s.location,
      lot_id: s.lot ? `lot-${s.lot}` : null,
      lot_code: s.lot ?? null,
      expiry_date: s.expiry ?? null,
      lot_blocked: s.lot ? false : null,
      on_hand: s.qty,
    }));
    const movements: StockMovement[] = rows.map((s, i) => ({
      id: 1000 - i,
      posted_at: opened,
      transaction_type: "adjustment_in",
      quantity: s.qty,
      entered_qty: s.item === "SC-M6" ? 12 : s.qty,
      entered_uom_code: s.item === "SC-M6" ? "BOX" : s.uom,
      warehouse_code: s.warehouse,
      location_code: s.location,
      lot_code: s.lot ?? null,
      source_document_type: "opening_balance",
      source_document_id: "demo",
      reason_code: "OPENING",
      reverses_id: null,
    }));
    const total = rows.reduce((sum, r) => sum + r.qty, 0);
    return { balances, balances_has_more: false, total_on_hand: total, movements };
  },
};
